import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, from } from 'rxjs';

/** How long after launch to start pulling route chunks in. */
const START_DELAY_MS = 5000;
/** Gap between chunks, so preloading never holds the main thread for long. */
const GAP_MS = 250;

/**
 * Preloads route chunks one at a time, well after launch.
 *
 * PreloadAllModules fetched and evaluated every lazy chunk — 144 of them, 2.69
 * MB — as soon as the first navigation finished. On a device that has run the
 * app before, the compiled-JS cache makes that cheap. On a fresh install there
 * is no such cache, so it all gets parsed and compiled on the main thread right
 * when the app is trying to show its first screen, which is why the first
 * launch after install was far slower than every launch after it.
 *
 * Navigations still end up warm; they just stop competing with startup.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloadStrategy implements PreloadingStrategy {
  private queue: Promise<unknown> = wait(START_DELAY_MS);

  preload(_route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const next = this.queue
      .then(() => wait(GAP_MS))
      .then(() => new Promise<unknown>(resolve => {
        // A failed preload must never break navigation — the route still loads
        // normally on demand.
        load().subscribe({ next: resolve, error: () => resolve(null), complete: () => resolve(null) });
      }));
    this.queue = next.catch(() => null);
    return from(next.catch(() => null));
  }
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

